import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Calendar } from 'lucide-react';
import MouseFollowEffect from './MouseFollowEffect';

const navigationLinks = [
  { to: '/', text: 'Home' },
  { to: '/services', text: 'Services' },
  { to: '/work', text: 'Portfolio' },
  { to: '/blog', text: 'Blog' },
  { to: '/about', text: 'About' },
  { to: '/schedule', text: 'Schedule Call', highlight: true },
  { to: '/contact', text: 'Contact' }
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      // Only blur the main content, not the menu
      document.querySelector('main')!.style.filter = 'blur(3px)';
      document.querySelector('footer')!.style.filter = 'blur(3px)';
    } else {
      document.body.style.overflow = '';
      document.querySelector('main')!.style.filter = 'none';
      document.querySelector('footer')!.style.filter = 'none';
    }
    return () => {
      document.body.style.overflow = '';
      document.querySelector('main')!.style.filter = 'none';
      document.querySelector('footer')!.style.filter = 'none';
    };
  }, [isMenuOpen]);

  return (
    <header className={`fixed top-0 left-0 right-0 bg-black/90 backdrop-blur-sm z-50 border-b border-gray-800 transition-all duration-300 ${isHomePage ? 'h-20' : 'h-16'}`}>
      <MouseFollowEffect className="bg-transparent h-full">
        <nav className="max-w-7xl mx-auto px-4 h-full">
          <div className={`flex items-center justify-between h-full`}>
            <NavLink 
              to="/" 
              className="flex items-center space-x-2 text-golden interactive z-50"
              onClick={() => setIsMenuOpen(false)}
              aria-label="DS Marketing Home"
            >
              <span className={`font-bold animate-[float_6s_ease-in-out_infinite] hover:animate-none ${isHomePage ? 'text-xl' : 'text-lg'}`} style={{ '--float-distance': '2px' }}>
                DS Marketing
              </span>
            </NavLink>
            
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-300 hover:text-golden transition-colors z-50"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <div className="hidden md:flex items-center space-x-4">
              {navigationLinks.slice(0, -2).map((link) => (
                <NavLink
                  key={link.text}
                  to={link.to}
                  className={({ isActive }) =>
                    `text-base font-medium transition-all duration-300 transform hover:scale-110 interactive ${
                      isActive ? 'text-golden' : 'text-gray-300 hover:text-golden'
                    }`
                  }
                  aria-label={link.text}
                >
                  {link.text}
                </NavLink>
              ))}
              
              <NavLink 
                to="/schedule"
                className={`inline-flex items-center px-4 py-2 border-2 border-golden text-golden rounded-full text-base font-semibold transition-all duration-300 transform hover:scale-110 hover:bg-golden hover:text-white interactive`}
                aria-label="Schedule a call with us"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Call
              </NavLink>
              
              <NavLink 
                to="/contact"
                className={`inline-flex items-center px-6 bg-golden text-white rounded-full text-base font-semibold transition-all duration-300 transform hover:scale-110 hover:bg-golden-600 interactive ${
                  isHomePage ? 'py-3' : 'py-2'
                }`}
                aria-label="Contact us"
              >
                Let's Talk
              </NavLink>
            </div>
          </div>

          <div
            className={`md:hidden fixed inset-0 top-16 bg-black/95 backdrop-blur-sm transition-all duration-300 ${
              isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
            }`}
            style={{ filter: 'none' }} // Ensure menu content is not blurred
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation menu"
          >
            <div className="flex flex-col p-4 space-y-2">
              {navigationLinks.map((link) => (
                <NavLink
                  key={link.text}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `py-3 px-4 text-lg font-medium rounded-lg transition-colors ${
                      link.highlight ? 'bg-golden/20 text-golden' : ''
                    } ${
                      isActive ? 'text-golden bg-golden/10' : 'text-gray-300 hover:bg-golden/10'
                    }`
                  }
                  aria-label={link.text}
                >
                  {link.text}
                </NavLink>
              ))}
            </div>
          </div>
        </nav>
      </MouseFollowEffect>
    </header>
  );
}